import { get } from "lodash";
import { NOTIFICATION_TYPES, SCREENS } from "utils/constants";

export const SYSTEM_NOTIFICATION_KINDS = {
  SEND_TO_MANAGEMENT_FAILED: "SEND_TO_MANAGEMENT_FAILED",
  SIGNED_INCOMPLETE: "SIGNED_INCOMPLETE",
  COMPLETE_DOCS_UNSENT: "COMPLETE_DOCS_UNSENT",
  WHATSAPP_REPLY: "WHATSAPP_REPLY",
};

const SYSTEM_NOTIFICATION_LABELS = {
  [SYSTEM_NOTIFICATION_KINDS.WHATSAPP_REPLY]: "VIEW_PROJECT",
};

const openSubscription = (notification, { navigate }) => {
  const subscriptionId = get(notification, "meta.subscriptionId");
  if (subscriptionId) {
    navigate(
      `${SCREENS.PRIVATE.BROKER.SUBSCRIPTIONS.DETAIL.path}/${subscriptionId}`,
    );
  }
};

const openContact = (notification, { navigate }) => {
  const contactId = get(notification, "meta.contactId");
  const projectId = get(notification, "meta.projectId");
  if (contactId) {
    const search = new URLSearchParams({ openContact: contactId });
    if (projectId) search.set("projectId", projectId);
    navigate(
      `${SCREENS.PRIVATE.BROKER.PROJECTS.LIST.path}?${search.toString()}`,
    );
  }
};

const SYSTEM_NOTIFICATION_ACTIONS = {
  [SYSTEM_NOTIFICATION_KINDS.SEND_TO_MANAGEMENT_FAILED]: openSubscription,
  [SYSTEM_NOTIFICATION_KINDS.SIGNED_INCOMPLETE]: openSubscription,
  [SYSTEM_NOTIFICATION_KINDS.COMPLETE_DOCS_UNSENT]: openSubscription,
  [SYSTEM_NOTIFICATION_KINDS.WHATSAPP_REPLY]: openContact,
};

/**
 * Maps each notification type to an action handler or null.
 * actionHandler(notification, { navigate }) is called on "action" click (banner or list).
 * null = no action (no action button or click does nothing).
 */
export const NOTIFICATION_TYPE_ACTIONS = {
  [NOTIFICATION_TYPES.TASK_REMINDER]: (notification, { navigate }) => {
    const taskId = get(notification, "meta.taskId");
    if (taskId) {
      navigate(`${SCREENS.PRIVATE.BROKER.TASKS.LIST.path}?task=${taskId}`);
    }
  },
  [NOTIFICATION_TYPES.MISSED_CALL]: openContact,
  [NOTIFICATION_TYPES.NEW_LEAD]: openContact,
  [NOTIFICATION_TYPES.SYSTEM]: (notification, context) => {
    const kind = get(notification, "meta.kind");
    const handler = SYSTEM_NOTIFICATION_ACTIONS[kind];
    if (handler) {
      handler(notification, context);
    }
  },
};

/**
 * Returns the action handler for a notification, taking into account both its
 * type and (for SYSTEM notifications) its `meta.kind`. Returns null when no
 * action is available — consumers can use this to decide whether to render an
 * "action" button.
 */
export const getNotificationActionLabelKey = (notification) => {
  const type = get(notification, "type");
  if (type === NOTIFICATION_TYPES.MISSED_CALL) {
    return "VIEW_CONTACT";
  }
  if (type === NOTIFICATION_TYPES.NEW_LEAD) {
    return "VIEW_PROJECT";
  }
  if (type === NOTIFICATION_TYPES.SYSTEM) {
    return (
      SYSTEM_NOTIFICATION_LABELS[get(notification, "meta.kind")] ||
      "VIEW_SUBSCRIPTION"
    );
  }
  if (type === NOTIFICATION_TYPES.TASK_REMINDER) {
    return "VIEW_TASK";
  }
  return null;
};

export const getNotificationAction = (notification) => {
  const type = get(notification, "type");
  const action = NOTIFICATION_TYPE_ACTIONS[type];
  if (typeof action !== "function") {
    return null;
  }
  if (type === NOTIFICATION_TYPES.SYSTEM) {
    const kind = get(notification, "meta.kind");
    if (!SYSTEM_NOTIFICATION_ACTIONS[kind]) {
      return null;
    }
  }
  return action;
};

export const canCallFromNotification = (notification, user) =>
  get(notification, "type") === NOTIFICATION_TYPES.NEW_LEAD &&
  get(user, "organization.config.aircall.enabled") === true &&
  Boolean(get(notification, "meta.phoneNumber"));
